import { useEffect, useState } from "react";
import { CreditCard, Loader2 } from "lucide-react";
import { Button } from "@baluu/ui/components/button";
import { Separator } from "@baluu/ui/components/separator";
import { useAuthSnapshot, hydrateSession } from "../../../../lib/auth-client";
import { authStore } from "../../../../stores/auth-store";
import { CustomerAuthDialog } from "../../../CustomerAuthDialog/CustomerAuthDialog";
import { BookingApiError, messageOf } from "../../api";
import { Notice } from "../../parts";
import { describeAccountSummary, describePayNowError, describeRowStatus, type RowTone } from "./accountCopy";
import { shortDate } from "./format";
import { formatMoney } from "../../money";
import { usePayNextDue } from "./useClassroom";
import type { CustomerAccount } from "./types";

const TONE: Record<RowTone, string> = {
  paid: "text-emerald-600",
  due: "text-muted-foreground",
  overdue: "text-destructive",
  muted: "text-muted-foreground",
};

/**
 * The receipt's payment block for a booking with a payment account: total,
 * paid, outstanding, one line per payment with its status and due date,
 * and "Pay now" for the next payment payable online. Port of v1's
 * AccountPaymentBlock. Pay now needs the customer signed in with the email
 * they booked with; signed out, it opens the sign-in (in this element's own
 * shadow-root portal) and carries on once they are in.
 */
export function AccountPaymentBlock(p: {
  api: string;
  businessSlug: string;
  /** The school's colour, for the sign-in dialog. */
  primaryColor?: string;
  bookingId: string;
  account: CustomerAccount;
  timezone?: string;
}) {
  const auth = useAuthSnapshot();
  const pay = usePayNextDue(p.api, p.bookingId);
  const [error, setError] = useState("");
  const [signingIn, setSigningIn] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const fmt = {
    money: (pence: number) => formatMoney(pence, p.account.currency),
    date: (iso: string) => shortDate(iso, p.timezone),
  };
  const summary = describeAccountSummary(p.account, fmt);

  useEffect(() => {
    void hydrateSession(p.api, p.businessSlug);
  }, [p.api, p.businessSlug]);

  async function payNow(token = auth.token) {
    setError("");
    if (!token) {
      setSigningIn(true);
      return;
    }
    try {
      const res = await pay.mutateAsync(token);
      if (res?.paymentUrl) {
        setRedirecting(true);
        window.location.assign(res.paymentUrl);
        return;
      }
      setError(describePayNowError(undefined, undefined));
    } catch (e) {
      const status = e instanceof BookingApiError ? e.status : undefined;
      setError(describePayNowError(status, messageOf(e, "") || undefined));
      if (status === 401) {
        // The stored session is dead: drop it and ask again.
        authStore.setAnonymous();
        setSigningIn(true);
      }
    }
  }

  const busy = pay.isPending || redirecting;

  return (
    <div className="space-y-2">
      <Line label="Total" value={fmt.money(p.account.totalPence)} strong />
      <Line label="Paid" value={fmt.money(p.account.paidPence)} className="text-emerald-600" />
      {p.account.overpaidPence > 0 ? (
        <Line label="Refund due to you" value={fmt.money(p.account.overpaidPence)} className="text-muted-foreground" />
      ) : (
        <Line label="Outstanding" value={fmt.money(p.account.outstandingPence)} className="text-muted-foreground" />
      )}

      {p.account.rows.length > 0 && (
        <>
          <Separator />
          <ul className="space-y-1.5">
            {p.account.rows.map((row, i) => {
              const status = describeRowStatus(row, fmt, { requested: p.account.requested });
              return (
                <li key={i} className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm">{row.label}</p>
                    {row.note && <p className="text-xs text-muted-foreground">{row.note}</p>}
                    <p className={`text-xs ${TONE[status.tone]}`}>{status.text}</p>
                  </div>
                  <span className="shrink-0 text-sm font-medium tabular-nums">{fmt.money(row.amountPence)}</span>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {summary && <p className="text-xs text-muted-foreground">{summary}</p>}

      {p.account.payNow && (
        <Button onClick={() => void payNow()} disabled={busy || auth.status === "unknown"} className="mt-1 w-full">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
          Pay {fmt.money(p.account.payNow.amountPence)} now
        </Button>
      )}
      {error && <Notice tone="error">{error}</Notice>}

      <CustomerAuthDialog
        open={signingIn}
        onOpenChange={setSigningIn}
        apiUrl={p.api}
        businessSlug={p.businessSlug}
        primaryColor={p.primaryColor}
        onSignedIn={() => {
          setSigningIn(false);
          void payNow(authStore.getToken());
        }}
      />
    </div>
  );
}

function Line({ label, value, strong, className = "" }: { label: string; value: string; strong?: boolean; className?: string }) {
  return (
    <div className={`flex items-center justify-between gap-3 ${className}`}>
      <span className={strong ? "text-sm font-semibold uppercase tracking-tight" : "text-sm font-medium"}>{label}</span>
      <span className={strong ? "text-base font-bold tabular-nums" : "text-sm font-medium tabular-nums"}>{value}</span>
    </div>
  );
}
